import { Component, Element, Prop, h } from '@stencil/core';
import { parseJsonProp } from '../../../../../utils/helpers/hydration';
import { lazyLoadImages } from '../../../../../utils/helpers/lazy-images';

const COMPONENT_NAME = 'dh-services-reviews-type-1';

interface ServiceReviewItem {
  icon: string;
  logoAlt: string;
  href: string;
  stars: number;
  logoWidth?: number;
  logoHeight?: number;
  name?: string;
  votes?: string;
  votesLabel?: string;
  variant?: string;
  qaTag?: string;
}

/**
 * Third-party platform ratings showcase — a row of linked review badges (G2, TrustPilot, Google Workspace, Chrome Web Store, etc.) with platform logo, optional title, star rating, and review count.
 * Desktop: equal-width bordered cards in a horizontal row inside a centered container; mobile and tablet: cards wrap into two columns then stack.
 * Decorative pastel glows are always rendered behind the cards.
 *
 * @category DocHub
 * @example
 *   <dh-services-reviews-type-1
 *     last-updated="02 Jun 2026"
 *     items='[{"icon":"https://cdn.mrkhub.com/dochub-frontend/224/images/_modules/services-reviews/g2crowd.svg","logoAlt":"DocHub Reviews","logoWidth":30,"logoHeight":30,"href":"https://www.g2.com/products/dochub/reviews","stars":4.5,"name":"DocHub Reviews","votesLabel":"238 reviews","variant":"g2","qaTag":"service-review--g2"}]'
 *   ></dh-services-reviews-type-1>
 */
@Component({
  tag: 'dh-services-reviews-type-1',
  styleUrl: 'index.scss',
  shadow: false,
})
export class DhServicesReviewsType1 {
  @Element() el!: HTMLElement;

  private cleanupLazyImages?: () => void;
  /**
   * Review platform badges. Each item renders a card linking to the platform
   * listing. Logo images are supplied by the integrator as absolute URLs.
   *
   * @type {array}
   * @item.type {object}
   * @item.property {image} icon - Platform logo image URL (absolute), supplied by the integrator
   * @item.property {string} logoAlt - Accessible alt text for the platform logo
   * @item.property {string} href - URL of the platform listing/reviews page
   * @item.property {number} stars - Star rating from 0 to 5; decimal values render partial star fill
   * @item.property {number} logoWidth - Intrinsic logo width in pixels
   * @item.property {number} logoHeight - Intrinsic logo height in pixels
   * @item.property {string} name - Platform title shown in the card header (e.g. "G2", "TrustPilot")
   * @item.property {string} votes - Optional legacy numeric figure; prefer `votesLabel` for the review count text
   * @item.property {string} votesLabel - Review count label shown after the score divider (e.g. "225 reviews", "14k reviews")
   * @item.property {string} variant - Optional modifier key matching `--<variant>` style hooks (e.g. "g2", "trustpilot", "google-workspace", "chrome-web-store")
   * @item.property {string} qaTag - Optional value for `data-qa-tag` to aid e2e selectors
   * @item.required icon
   * @item.required logoAlt
   * @item.required href
   * @item.required stars
   * @item.content logoAlt name votes votesLabel
   * @minItems 1
   * @maxItems 6
   */
  @Prop({ mutable: true }) items!: ServiceReviewItem[] | string;

  /**
   * Small footer label, typically a "last updated" date.
   * @required
   * @content
   */
  @Prop({ attribute: 'last-updated' }) lastUpdated!: string;

  /**
   * Tuck the decorative glow BEHIND the preceding block. The glow overhangs upward above the
   * section; by default (`false`) it sits in front so it's always fully visible. Set to `true`
   * only when this block follows a full-bleed opaque block (e.g. the `dh-form-preview-type-1`
   * hero) that should cover the overhang — then the glow drops to `z-index: -1` so the block
   * above paints over it. Leave `false` everywhere else, otherwise a preceding block would clip
   * the glow.
   * @default false
   */
  @Prop({ attribute: 'glow-behind' }) glowBehind: boolean = false;

  componentWillLoad() {
    this.items = parseJsonProp(this.items, []);
  }

  private formatScore(stars: number): string {
    return (Math.round(stars * 10) / 10).toFixed(1);
  }

  private getReviewLabel(item: ServiceReviewItem): string | undefined {
    if (item.votesLabel) {
      return item.votesLabel;
    }

    if (item.votes) {
      return item.votes;
    }

    return undefined;
  }

  private renderStars(stars: number) {
    return (
      <dh-rating-stars
        class={`${COMPONENT_NAME}__stars`}
        score={stars}
        label={`Rated ${this.formatScore(stars)} out of 5`}
      />
    );
  }

  componentDidLoad() {
    // Defer below-the-fold images to an IntersectionObserver instead of relying
    // on native loading="lazy" alone (see the lazy-images helper).
    this.cleanupLazyImages = lazyLoadImages(this.el);
  }

  disconnectedCallback() {
    this.cleanupLazyImages?.();
  }

  render() {
    const items: ServiceReviewItem[] = Array.isArray(this.items) ? this.items : [];

    if (items.length === 0) {
      return null;
    }

    return (
      <dh-section class={COMPONENT_NAME} noContainer yGap="3xl" yGapSm="5xl" yGapLg="5xl" role="region">
        <div class={`${COMPONENT_NAME}__inner dh-section--container`}>
          <div
            class={{
              [`${COMPONENT_NAME}__glow`]: true,
              [`${COMPONENT_NAME}__glow--behind`]: this.glowBehind,
            }}
            aria-hidden="true"
          >
            <div class={`${COMPONENT_NAME}__shadow ${COMPONENT_NAME}__shadow--1`} />
            <div class={`${COMPONENT_NAME}__shadow ${COMPONENT_NAME}__shadow--2`} />
            <div class={`${COMPONENT_NAME}__shadow ${COMPONENT_NAME}__shadow--3`} />
          </div>

          <div class={`${COMPONENT_NAME}__grid`}>
            {items.map(item => {
              const cardModifier = item.variant ? ` ${COMPONENT_NAME}__card--${item.variant}` : '';
              const reviewLabel = this.getReviewLabel(item);

              return (
                <div class={`${COMPONENT_NAME}__item`}>
                  <a
                    class={`${COMPONENT_NAME}__card${cardModifier}`}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-qa-tag={item.qaTag}
                  >
                    <div class={`${COMPONENT_NAME}__header`}>
                      <div class={`${COMPONENT_NAME}__icon`}>
                        <img
                          src={item.icon}
                          alt={item.logoAlt}
                          width={item.logoWidth ?? 32}
                          height={item.logoHeight ?? 32}
                          loading="lazy"
                          decoding="async"
                        />
                      </div>
                      {item.name && <div class={`${COMPONENT_NAME}__title`}>{item.name}</div>}
                    </div>

                    <div class={`${COMPONENT_NAME}__rating`}>
                      {this.renderStars(item.stars)}
                      {reviewLabel && <span class={`${COMPONENT_NAME}__reviews`}>{reviewLabel}</span>}
                    </div>
                  </a>
                </div>
              );
            })}
          </div>

          <div class={`${COMPONENT_NAME}__footer`}>{this.lastUpdated}</div>
        </div>
      </dh-section>
    );
  }
}
